import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createIncidentTypeV1,
  deleteIncidentTypeV1,
  updateIncidentTypeV1,
  type MatterSettings,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy, sortBy } from "lodash";
import toast from "react-hot-toast";

export const useCreateIncidentType = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createIncidentTypeV1>[0]) =>
      createIncidentTypeV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData([queryKeys.matterSettings]);
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              incidentTypes: sortBy(
                [
                  ...(oldData.data.incidentTypes ?? []),
                  { ...data, id: "temp" },
                ],
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error creating incident type");
      }
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Incident type created successfully");
      }
    },
  });
};

export const useUpdateIncidentType = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateIncidentTypeV1>[0]) =>
      updateIncidentTypeV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData([queryKeys.matterSettings]);
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;

          return {
            data: {
              ...oldData.data,
              incidentTypes: orderBy(
                (oldData.data.incidentTypes ?? []).map((group) =>
                  group.id === data.id ? { ...group, ...data } : group,
                ),
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error updating incident type");
      }
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Incident type updated successfully");
      }
    },
  });
};

export const useDeleteIncidentType = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteIncidentTypeV1>[0]) =>
      deleteIncidentTypeV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData([queryKeys.matterSettings]);
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              incidentTypes: (oldData.data.incidentTypes ?? []).filter(
                (group) => group.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error deleting incident type");
      }
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Incident type deleted successfully");
      }
    },
  });
};
